import { newCombatAutoFireKey, newFareKey } from './seaFareKeys.ts';
import { captureAuthRequestContext, type AuthRequestContext } from './authHeader.ts';
import type { JuSpendContext } from './billing/ju.ts';
import type { FareOutcome } from '../sea/autopilotFare.ts';

export type SeaFareKind = 'autopilot' | 'combat';
export interface SeaFareOwner<Scene extends object> {
  readonly scene: Scene;
  readonly account: object;
  readonly life: object;
  readonly viewerId: string | null;
}
interface FareModule { pay(key: string, context: JuSpendContext): Promise<FareOutcome> }
export interface SeaFareResult<Scene extends object> {
  readonly scene: Scene;
  readonly outcome: FareOutcome;
  /** Recheck immediately before applying UI/scene effects in a promise callback. */
  isCurrent(): boolean;
}
interface Options<Scene extends object> {
  owner(): SeaFareOwner<Scene> | null;
  load?(kind: SeaFareKind): Promise<FareModule>;
  newKey?(kind: SeaFareKind): string;
  capture?(): Promise<AuthRequestContext | null>;
}
async function loadFare(kind: SeaFareKind): Promise<FareModule> {
  if (kind === 'autopilot') {
    const module = await import('../sea/autopilotFare.ts');
    return { pay: (key, context) => module.payAutopilotFare(key, undefined, context) };
  }
  const module = await import('../sea/seaCombatAutoFire.ts');
  return { pay: (key, context) => module.payCombatAutoFire(key, undefined, context) };
}

/** Explicit-entry fare loading with one pending/key per SKU and owned sea life.
 * The canonical ledger operation remains unchanged. Once its RPC has started,
 * invalidation can reject the result but cannot cancel that server operation. */
export function createLazySeaFares<Scene extends object>(options: Options<Scene>) {
  type Entry = { owner: SeaFareOwner<Scene>; key: string; attempt: object | null; abort: AbortController | null;
    current: (() => boolean) | null; promise: Promise<SeaFareResult<Scene> | null> | null };
  const entries = new Map<SeaFareKind, Entry>();
  const ownerNow = () => { try { return options.owner(); } catch { return null; } };
  const same = (a: SeaFareOwner<Scene>, b: SeaFareOwner<Scene> | null) =>
    !!b && a.scene === b.scene && a.account === b.account && a.life === b.life && a.viewerId === b.viewerId;
  return {
    invalidate() { for (const entry of entries.values()) entry.abort?.abort(); entries.clear(); },
    run(kind: SeaFareKind): Promise<SeaFareResult<Scene> | null> {
      const owner = ownerNow();
      if (!owner) return Promise.resolve(null);
      let entry = entries.get(kind);
      if (!entry || !same(entry.owner, owner)) {
        entry?.abort?.abort();
        entry = { owner, key: options.newKey?.(kind) ?? (kind === 'autopilot' ? newFareKey() : newCombatAutoFireKey()), attempt: null, abort: null, current: null, promise: null };
        entries.set(kind, entry);
      }
      // Native auth can revoke an epoch before the user-store owner changes.
      // An explicit retry captures fresh auth while retaining this action key.
      if (entry.promise && entry.current && !entry.current()) entry.promise = null;
      if (entry.promise) return entry.promise;
      const request = entry;
      const attempt = {}; request.attempt = attempt;
      request.abort?.abort();
      const abort = new AbortController(); request.abort = abort;
      let authCurrent: (() => boolean) | null = null;
      const current = () => {
        try { return entries.get(kind) === request && request.attempt === attempt && !abort.signal.aborted
          && same(request.owner, ownerNow()) && (!authCurrent || authCurrent()); }
        catch { return false; }
      };
      request.current = current;
      // Reserve before calling an injected/lazy loader: it may synchronously
      // reenter, and rapid clicks must share the same payment and ledger key.
      request.promise = Promise.resolve().then(async () => {
        let outcome: FareOutcome;
        try {
          const context = await (options.capture ?? captureAuthRequestContext)();
          if (!current()) return null;
          if (!context) throw new Error('Account authorization unavailable.');
          authCurrent = context.isCurrent;
          if (!current() || context.viewerId !== owner.viewerId) return null;
          const module = await (options.load ?? loadFare)(kind);
          if (!current()) return null;
          outcome = await module.pay(request.key, { viewerId: context.viewerId, headers: context.headers,
            isCurrent: current, signal: abort.signal });
        } catch { outcome = { kind: 'failed' }; }
        if (!current()) return null;
        if (outcome.kind !== 'free' && outcome.kind !== 'paid') request.promise = null;
        return { scene: request.owner.scene, outcome, isCurrent: current };
      });
      return request.promise;
    },
  };
}
